import { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { blockedBy, msLabel } from '../outline/blocked.js'
const FIELD_REPORT_TYPE_ID = 'recd6NVQcupxuwvJt'   // the ad-hoc Field Report type (constants.js FIELD_OBSERVATION_MS_TYPE_IDS)
import { POST_PERMIT_TYPE_IDS, POST_REVISION_TYPE_IDS, SPEC_RELEASE_MS_TYPE_IDS, SCHEDULE_RELEASE_MS_TYPE_IDS, FIELD_OBSERVATION_MS_TYPE_IDS, HIDDEN_CL_CATEGORIES, PRE_DESIGN_RESEARCH_TYPE_ID, RENDERING_PACKAGE_TYPE_ID, selectionsDocKinds, SELECTIONS_DOC_LABELS } from '../outline/constants.js'

const PPC_DRAWINGS_PERMIT_REVISION_TYPE_ID = 'recGcYRX5M5nr7xS9' // PPC Drawings Permit Revision
import { postMilestone, getAgencyApprovals, getDeltaRevisions, getMilestoneRecord, getProjectMilestones } from '../services/milestones.js'
import { previewFinishCascade, syncFinishAndCascade } from '../services/schedule.js'
import { getMilestoneDocuments } from '../services/documents.js'
import { getMilestoneChecklistItems, completeChecklistItem, markChecklistItemNotRequired } from '../services/checklist.js'
import { getMilestoneSheetRevisions } from '../services/sheets.js'
import { getRevisionQcSummaries } from '../services/qcVersions.js'
import { issueMilestoneSpec, getMilestoneSpecSections, getSpecChanges, saveSpecReleaseNotes } from '../services/specSections.js'
import { issueMilestoneSchedule, getMilestoneScheduleItems } from '../services/schedules.js'
import { freezePreDesignReport } from '../services/predesign.js'
import { issueSelectionsDocs, incompleteSelectionsDocs } from '../services/selectionsDocuments.js'
import { getOpenNoteCounts } from '../services/documentMarkup.js'
import { freezeOpcReport } from '../services/opc.js'
import { freezeSiteAnalysis } from '../services/siteAnalysis.js'
import { freezeFieldObservation } from '../services/fieldObservations.js'
import { burnMilestoneIssuance, getMilestoneSendStatus } from '../services/sheetIssuance.js'
import { getOrCreateLink, upsertEntry, listEntries } from '../services/journey.js'
import { isClientFacingDeliverable, isSheetGateMilestone } from '../journey/eligibility.js'
import { alertDialog, confirmDialog, promptDialog } from './Dialog.jsx'
import { isSeatLoaded, hasModule } from '../services/tenancy.js'
import { getPassengersFor, crewMovePassenger, movePassenger, getCandidateMilestones, scheduleOrderedOptions } from '../services/passengers.js'
import { closeThread } from '../services/threads.js'
import { getFeedersWaitingOn, markFeedersSent, FEEDER_HANDOFF, daysOutLabel } from '../services/feeders.js'
import { getPostingPermitContext, reportActivitiesLogged, activityPlain, getClientReach } from '../services/permitting.js'
import { confirmClientUpdate } from './confirmClientUpdate.js'
import { logPermitActivity, createPermit, createAgencyReview, PERMIT_TYPES } from '../services/permits.js'
import { getOrganizations } from '../services/design.js'
import Select from './Select.jsx'
import { refreshMsPassengers } from './MilestoneSections.jsx'
import { ExpectedSourcePick } from './MilestoneFeederSection.jsx'
import { useScrollLock } from '../hooks/useScrollLock.js'
import PostChainPanel, { earlierSteps, MeetingApprovalFields, CLIENT_REVIEW_TYPE_ID } from './PostChainPanel.jsx'
import { recordPost } from '../services/milestoneFlow.js'

const getFirst = v => (Array.isArray(v) ? v[0] : v)

function todayStr() {
  const d  = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return d.getFullYear() + '-' + mm + '-' + dd
}

const MONTHS_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function fmtMonthDay(ymd) {
  if (!ymd) return ''
  const p = String(ymd).slice(0, 10).split('-')
  if (p.length !== 3) return ymd
  return MONTHS_ABBR[parseInt(p[1], 10) - 1] + ' ' + parseInt(p[2], 10)
}

function ModalLabel({ children }) {
  return (
    <div style={{
      fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500,
      letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)',
      marginBottom: 6, marginTop: 14
    }}>
      {children}
    </div>
  )
}

/* ── Searchable linked-record picker ──────────────────────────────────────── */
function LinkedPicker({ records, value, onChange, invalid }) {
  const [open, setOpen]     = useState(false)
  const [search, setSearch] = useState('')
  const wrapRef             = useRef(null)

  const selected = records.find(r => r.id === value)
  const filtered = search
    ? records.filter(r => r.name.toLowerCase().includes(search.toLowerCase()))
    : records

  useEffect(() => {
    function onOutside(e) {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setSearch('')
      }
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [open])

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginBottom: 4 }}>
      <div
        onClick={() => { setOpen(o => !o); setSearch('') }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 10px',
          border: `0.5px solid ${invalid ? '#c0392b' : 'var(--aj-border-mid)'}`,
          borderRadius: open ? '4px 4px 0 0' : 4,
          cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13,
          background: 'var(--aj-bg-subtle)', userSelect: 'none', transition: 'border-color 0.15s'
        }}
      >
        <span style={{
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: selected ? 'var(--aj-text)' : 'var(--aj-text-muted)'
        }}>
          {selected?.name || 'Select…'}
        </span>
        <span style={{ fontSize: 10, color: 'var(--aj-text-muted)', flexShrink: 0, marginLeft: 6 }}>▾</span>
      </div>

      {open && (
        <div style={{
          position: 'absolute', left: 0, right: 0, zIndex: 10,
          border: '0.5px solid var(--aj-border-mid)', borderTop: 'none',
          borderRadius: '0 0 4px 4px', background: 'var(--aj-bg-elevated)',
          maxHeight: 160, overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.10)'
        }}>
          <input
            autoFocus type="text" placeholder="Search…" value={search}
            onChange={e => setSearch(e.target.value)}
            onClick={e => e.stopPropagation()}
            style={{
              display: 'block', width: '100%', boxSizing: 'border-box',
              padding: '7px 10px', border: 'none',
              borderBottom: '0.5px solid var(--aj-border)',
              fontFamily: '"DM Sans",sans-serif', fontSize: 13,
              color: 'var(--aj-text)', outline: 'none', background: 'var(--aj-bg-subtle)'
            }}
          />
          {filtered.length === 0 ? (
            <div style={{ padding: '8px 10px', fontFamily: '"DM Sans",sans-serif',
              fontSize: 13, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>
              {records.length ? 'No matches' : 'None found'}
            </div>
          ) : filtered.map(rec => (
            <div key={rec.id}
              onClick={e => { e.stopPropagation(); onChange(rec.id); setOpen(false); setSearch('') }}
              style={{
                padding: '7px 10px', fontFamily: '"DM Sans",sans-serif',
                fontSize: 13, color: 'var(--aj-text)', cursor: 'pointer',
                background: rec.id === value ? 'var(--aj-bg-subtle)' : ''
              }}
              onMouseOver={e => { e.currentTarget.style.background = 'var(--aj-bg-subtle)' }}
              onMouseOut={e => { e.currentTarget.style.background = rec.id === value ? 'var(--aj-bg-subtle)' : '' }}
            >
              {rec.name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Main modal ───────────────────────────────────────────────────────────── */
export default function PostMilestoneModal() {
  const [open, setOpen]               = useState(false)
  const [msId, setMsId]               = useState(null)
  // Set true on the SUCCESS path immediately before close() — read and cleared inside close().
  const postedRef                     = useRef(false)
  const [date, setDate]               = useState('')
  // The compressed chain (docs/COMPRESSED_CHAIN.md): the approving meeting on a Client Review, and the
  // note carried over when the steps before it were just posted together.
  const [approval, setApproval]       = useState({})
  const [chainNote, setChainNote]     = useState('')
  const [, setChainTick]              = useState(0)
  // A Field Report's visit may have been one of the construction reviews (Matt, 2026-10-25: Agan's reviews
  // were never posted though its Field Reports were). Picked here, that review's post window opens next.
  const [alsoReview, setAlsoReview]   = useState('')
  const [reviewOpts, setReviewOpts]   = useState(null)
  // Opened from a captain's report instruction (2026-10-22): above the lockout, with the tower's words
  // shown where the date is set ("back-date it to the day we presented").
  const [zTop, setZTop]               = useState(null)
  const [instr, setInstr]             = useState('')
  // The posted date is ALWAYS the finish (Matt, 2026-09-24): the old 'Keep the schedule as-is' default
  // threw away every early finish (Verheyen's DD BIM posted 13 days early, successors still waited).
  const [syncChoice, setSyncChoice]   = useState('sync')
  const [agencyRecords, setAgencyRecords]       = useState(null)
  const [planCheckRecords, setPlanCheckRecords] = useState(null)
  const [permitRevRecords, setPermitRevRecords] = useState(null)
  const [agencyId, setAgencyId]           = useState('')
  const [planCheckId, setPlanCheckId]     = useState('')
  const [permitRevId, setPermitRevId]     = useState('')
  const [agencyInvalid, setAgencyInvalid]         = useState(false)
  const [planCheckInvalid, setPlanCheckInvalid]   = useState(false)
  const [permitRevInvalid, setPermitRevInvalid]   = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')
  // OPEN CHECKLIST ITEMS, IN PLACE (Matt, 2026-10-27: "what's the point of having it if it's just going
  // to get me stuck?"): the post refusal lists them here with Done / Not required, so posting never
  // means leaving this window.
  const [openCl, setOpenCl] = useState(null)   // null | [{ id, name, busy }]
  const [journeyChoice, setJourneyChoice] = useState('none')   // 'none' | 'draft' | 'final' | 'silent'
  // Rendering Package gate: null = not checked, 'checking', 'has-images', 'no-images'
  const [rpImageCheck, setRpImageCheck] = useState(null)
  // Passengers (2026-09-09, docs/PASSENGERS.md): open threads riding this plane block posting.
  const [passengerGate, setPassengerGate] = useState('ok')   // 'checking' | 'ok' | 'blocked'
  const [passengers, setPassengers] = useState([])
  // AlderCAD (bim-plan/28 round 9): sheets sent from AlderCAD whose model changed after the send
  const [staleSends, setStaleSends] = useState([])
  // Feeders this post sends (docs/OPERATING_MODEL.md, 2026-09-23): posting a submission IS submitting
  // it to the City, so the feeder after it is marked sent on the posted date and this window asks when
  // it's expected back (required, even as a guess) and whether that date came from them or from us.
  // null = loading; [] = none. handoffDates: { [feederMsId]: { expectedOn, source } }.
  const [handoffs, setHandoffs] = useState(null)
  const [handoffDates, setHandoffDates] = useState({})
  // Permit activity this post logs (build step 4 slice B, Matt 2026-09-23): posting a submission logs
  // "Submitted to Agency" on the agency review(s) in Permits, posting an Agency Review logs what came
  // back. Shown like the Permits log form, with the type, date and next date locked to the posting.
  // null = loading; { kind: null } = nothing to log.
  const [permitCtx, setPermitCtx] = useState(null)
  const [permitPick, setPermitPick] = useState({})      // reviewId -> true
  const [permitMemo, setPermitMemo] = useState('')
  // 'email' | 'silent' — silent updates the client's Permitting page without emailing subscribers
  // (the Permits log form's same choice). Its own choice, since the milestones that log a permit
  // activity aren't Client Hub milestones and never show the "post silently" option.
  const [permitSend, setPermitSend] = useState('email')
  const [permitNextDate, setPermitNextDate] = useState('')
  // No agency review, no post (Matt, 2026-09-23): the activity has to be logged, so the agency review
  // can be created right here — pick or add the permit, pick the agency.
  const [addReview, setAddReview] = useState({ open: false, permitId: '', newType: '', orgId: '' })
  const [orgs, setOrgs] = useState(null)
  const [addBusy, setAddBusy] = useState(false)
  // Drawing-set gate: null = not checked, 'checking', 'ok', 'blocked'
  const [sheetGateCheck, setSheetGateCheck] = useState(null)
  const [sheetGateMsg, setSheetGateMsg]     = useState('')
  // Fallback when msId isn't in ctx.state.milestones — the outline's own cache, scoped to
  // whatever project is currently zoomed into there, which has nothing to do with what's loaded
  // on the Flight Board. Without this, `ms` below silently resolved to undefined for any
  // milestone whose project wasn't already open in the outline, and the gating effect a little
  // further down (`if (!open || !ms) return`) never ran at all — the modal opened as an empty
  // 400px shell with nothing in it, no error, no loading state (2026-09-04, caught live: Matt
  // tried to post LUIS-DD from the Flight Board and got exactly this). null = not needed or not
  // yet resolved; 'error' = the fetch itself failed.
  const [fallbackMs, setFallbackMs] = useState(null)

  // Reference-counted (hooks/useScrollLock.js) — this can open ON TOP of a workspace (Pulse's
  // Flight Tracker drags a flight to the far right and opens this directly) that already holds
  // its own lock; a raw set/restore here would clear that lock on this modal's own close.
  useScrollLock(open)

  /* Derive ms and variant — the outline's cache first, the direct fetch as fallback (see
     fallbackMs's own comment above) */
  const ms       = msId ? ((ctx.state?.milestones || []).find(m => m.id === msId) || (fallbackMs !== 'error' ? fallbackMs : null)) : null
  const msName   = ms?.fields['Full Deliverable Name'] || ms?.fields['Name'] || 'Milestone'
  const delType  = (ms?.fields['Name (from Deliverable)'] || [])[0] || ''
  const delTypeId = (ms?.fields['Deliverable'] || [])[0] || ''
  // The milestone's OWN linked project, first — every `ctx.recordId || wf.zoomProjectId` below
  // used to assume the outline was zoomed into the same project as the milestone being posted,
  // which is only true when this modal was opened FROM the outline. Opened from the Flight Board
  // (a different project may — or may not — happen to be zoomed in the outline behind it), that
  // assumption was silently wrong: several best-effort side effects here (schedule cascade, spec/
  // schedule issuance, report freezes, journey linking) would have run against the WRONG
  // project. Falls back to the outline's own zoom state only if the milestone record somehow
  // carries no Project link at all (shouldn't happen).
  const msProjectId = getFirst(ms?.fields?.['Project']) || ctx.recordId || wf.zoomProjectId
  const isPermit        = POST_PERMIT_TYPE_IDS.includes(delTypeId)
  const isRevision      = POST_REVISION_TYPE_IDS.includes(delTypeId)
  // Of the revision types, exactly one is permit-flavored — the rest are all plan-check-flavored
  // (PPC Drawings Plan Check Revision, DAPC Drawings Revisions, Plan Check Resubmission). Each
  // revision milestone links to ONE matching Delta Revision kind, never both — see the JSX below,
  // which shows only the picker matching this milestone's own type (2026-07-23 — previously both
  // pickers always showed regardless of type, which was confusing on a plan-check milestone).
  const isPermitRevisionMs = delTypeId === PPC_DRAWINGS_PERMIT_REVISION_TYPE_ID
  const isRenderingPkg  = delTypeId === RENDERING_PACKAGE_TYPE_ID
  // Client-facing deliverable → offer to add it to the Client Hub as part of posting.
  const journeyEligible = isClientFacingDeliverable(delTypeId)

  // Posted-date ↔ schedule (LFT) relationship. If they match, there's nothing to ask. If they differ,
  // offer to sync the finish to the posted date and show the downstream impact (computed via the same
  // cascade the Gantt/nightly use). The legacy "Sync Latest Finish to Post Date" checkbox is retired.
  const currentLft = ms?.fields?.['Latest Finish (LFT)'] ? String(ms.fields['Latest Finish (LFT)']).slice(0, 10) : ''
  const lftDiffers = !!(date && currentLft && date !== currentLft)
  const cascadePreview = useMemo(() => {
    if (!lftDiffers) return null
    try { return previewFinishCascade(msId, date, ctx.state?.milestones || []) } catch { return null }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msId, date, lftDiffers])

  const pickersReady = (!isPermit && !isRevision)
    || (isPermit   && agencyRecords !== null)
    || (isRevision && planCheckRecords !== null && permitRevRecords !== null)

  /* Listen for open event */
  useEffect(() => {
    function onOpen(e) {
      const id = e.detail.msId
      setMsId(id)
      setZTop(e.detail.zIndex || null)
      setInstr(e.detail.instruction || '')
      setDate(e.detail.date || todayStr())
      setApproval({}); setChainNote('')
      setAlsoReview(''); setReviewOpts(null)
      setSyncChoice('sync')
      setJourneyChoice('none')
      setSaving(false)
      setError('')
      setRpImageCheck(null)
      setStaleSends([])
      getMilestoneSendStatus(id).then((list) => setStaleSends((list || []).filter((s) => s.changed))).catch(() => {})
      setSheetGateCheck(null); setSheetGateMsg('')
      setAgencyInvalid(false); setPlanCheckInvalid(false); setPermitRevInvalid(false)
      // Reset from whatever the PREVIOUS milestone's fallback was — stale data from a prior open
      // must never leak into this one while the check below (or its fetch) is still pending.
      setFallbackMs(null)
      if (!(ctx.state?.milestones || []).some(m => m.id === id)) {
        getMilestoneRecord(id).then(rec => setFallbackMs(rec || 'error')).catch(() => setFallbackMs('error'))
      }
      setOpen(true)
    }
    window.addEventListener('open-post-ms-modal', onOpen)
    return () => window.removeEventListener('open-post-ms-modal', onOpen)
  }, [])

  /* Load async data when modal opens */
  useEffect(() => {
    if (!open || !ms) return
    const projId = msProjectId || ''
    if (delTypeId === FIELD_REPORT_TYPE_ID && projId) {
      getProjectMilestones(projId).then(list => setReviewOpts((list || []).filter(m => {
        const n = (m.fields['Name (from Deliverable)'] || [])[0] || m.fields['Name'] || ''
        return !m.fields['Posted Date'] && /^(construction kick-?off|foundation|framing|rough-?in|envelope|finish layout|substantial completion)/i.test(String(n))
      }).map(m => ({ id: m.id, name: m.fields['Full Deliverable Name'] || m.fields['Name'] || 'Review' })))).catch(() => setReviewOpts([]))
    }

    if (isPermit) {
      setAgencyRecords(null)
      getAgencyApprovals(projId).then(records => {
        setAgencyRecords(records)
        const ex = (ms.fields['Agency Approvals'] || [])[0] || ''
        if (ex) setAgencyId(ex)
      })
    } else if (isRevision) {
      setPlanCheckRecords(null); setPermitRevRecords(null)
      getDeltaRevisions(projId).then(({ planCheck, permit }) => {
        setPlanCheckRecords(planCheck)
        setPermitRevRecords(permit)
        const exPC = (ms.fields['Delta Revisions - Plan Check'] || [])[0] || ''
        const exPR = (ms.fields['Delta Revisions - Permit']     || [])[0] || ''
        if (exPC) setPlanCheckId(exPC)
        if (exPR) setPermitRevId(exPR)
      })
    } else {
      setAgencyRecords(null); setPlanCheckRecords(null); setPermitRevRecords(null)
    }

    // Rendering Package: require at least one image document before allowing post.
    if (isRenderingPkg) {
      const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'avif'])
      setRpImageCheck('checking')
      getMilestoneDocuments(msId).then(res => {
        const hasImages = (res.documents || []).some(d => IMAGE_EXTS.has((d.file_type || '').toLowerCase()))
        setRpImageCheck(hasImages ? 'has-images' : 'no-images')
      }).catch(() => setRpImageCheck('has-images'))  // fail-open on error
    }

    // Drawing sheet QC gate: same check handleConfirm enforces, run up front so the modal opens
    // straight into a blocked screen instead of the full form when the milestone isn't postable
    // yet. The underlying requirement is universal — ANY milestone with sheet revisions attached
    // must have them QC-approved before posting, not just the curated SHEET_GATE_MS_TYPES list,
    // since a sheet can be carried onto any milestone sitting in a drawing-production phase (see
    // MilestoneSections.jsx's SHEET_PHASE_MAP/SHEET_TYPE_MAP) — 2026-08, closes the gap where a
    // non-curated-type milestone could post sheets with no PDF and no QC round ever started.
    //
    // UX split so this doesn't add a "checking…" flash to every single milestone type's post
    // modal, most of which never have sheets at all: the curated types keep the exact prior
    // behavior (block the form behind 'checking' until resolved — their whole purpose is a
    // drawing release, so the wait is expected). Every other type shows the form immediately
    // ('ok') and the check runs in the background, only flipping to 'blocked' — replacing an
    // already-visible form — in the rare case it actually finds unapproved sheets.
    // Passengers: any open thread aboard blocks the post. Fails open if the read fails (the
    // confirm re-checks live and is the real enforcement).
    // Feeders this post sends. Fails open: if the read fails, posting still works and the feeder can
    // be marked sent by hand from its own node.
    setHandoffs(null); setHandoffDates({})
    getFeedersWaitingOn(msId).then(list => setHandoffs(list || [])).catch(() => setHandoffs([]))

    // Permit activity this post logs. Fails open to "nothing to log" so a read error never blocks a
    // post; the Permits log form's own duplicate warning still protects against a double entry.
    setPermitCtx(null); setPermitPick({}); setPermitMemo(''); setPermitNextDate(''); setPermitSend('email')
    setAddReview({ open: false, permitId: '', newType: '', orgId: '' }); setAddBusy(false)
    getPostingPermitContext(msId, todayStr()).then(c => {
      setPermitCtx(c || { kind: null })
      const already = new Set(((c && c.alreadyLogged) || []).map(a => a.reviewId))
      const rs = ((c && c.reviews) || []).filter(r => !already.has(r.id))
      const pre = rs.filter(r => r.linked)
      const pick = {}
      ;(pre.length ? pre : (rs.length === 1 ? rs : [])).forEach(r => { pick[r.id] = true })
      setPermitPick(pick)
    }).catch(() => setPermitCtx({ kind: null }))

    setPassengerGate('checking'); setPassengers([])
    getPassengersFor([msId]).then(r => {
      const list = (r.byMilestone || {})[msId] || []
      setPassengers(list); setPassengerGate(list.length ? 'blocked' : 'ok')
    }).catch(() => setPassengerGate('ok'))

    const gateType = isSheetGateMilestone(delTypeId)
    setSheetGateCheck(gateType ? 'checking' : 'ok')
    ;(async () => {
      try {
        const sheetRevs = await getMilestoneSheetRevisions(msId)
        if (!sheetRevs || sheetRevs.length === 0) {
          if (gateType) {
            setSheetGateMsg('Drawing sheets must be added to this milestone before it can be posted.')
            setSheetGateCheck('blocked')
          }
          return
        }
        const qcSum = await Promise.race([
          getRevisionQcSummaries(sheetRevs.map(r => r.id)),
          new Promise((_, rej) => setTimeout(() => rej(new Error('qc-timeout')), 12000)),
        ])
        const unapproved = sheetRevs.filter(r => !(qcSum[r.id] && qcSum[r.id].approved))
        if (unapproved.length) {
          setSheetGateMsg(unapproved.length + ' drawing sheet' + (unapproved.length === 1 ? '' : 's') + ' must be cleared through QC before this milestone can be posted.')
          setSheetGateCheck('blocked')
          return
        }
        setSheetGateCheck('ok')
      } catch {
        // Non-gate types fail OPEN here (they were already showing 'ok' before this ran, and most
        // never touch sheets at all) — handleConfirm re-runs the same check for real at submit
        // time and is the actual enforcement point. Gate types keep failing closed, as before.
        if (gateType) {
          setSheetGateMsg('Could not verify drawing sheet QC status. Close and try again.')
          setSheetGateCheck('blocked')
        }
      }
    })()
  // `!!ms` is deliberately in this list (not just [open, msId]) — 2026-09-04: when `ms` isn't in
  // the outline's cache yet, it starts null and this whole effect no-ops on the `!ms` guard
  // above; the fallback fetch (see fallbackMs's own comment) resolves it a moment later, but
  // without `!!ms` here that never re-triggered this effect, so nothing it sets up (the sheet
  // gate, the permit/revision pickers) ever ran — the modal was stuck on an empty shell forever
  // even after the fallback succeeded (caught live: Matt still saw it stuck after the first
  // fallback-fetch fix shipped). A boolean proxy, not `ms` itself, so this doesn't re-run on
  // every render just because `ms` is a new object reference.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, msId, !!ms])

  /* Escape key */
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function close() { setOpenCl(null);
    // `posted` lets a listener tell a real post from an abandoned one. close() runs on BOTH paths,
    // so without this flag they are indistinguishable — and milestone fast-track has to tell them
    // apart, because it rolls back the checklist items it auto-completed if the post is abandoned.
    // See FlightPlanTab#doFastTrackMilestone.
    const posted = postedRef.current
    const closingMsId = msId
    postedRef.current = false
    setOpen(false); setMsId(null)
    setAgencyRecords(null); setPlanCheckRecords(null); setPermitRevRecords(null)
    setAgencyId(''); setPlanCheckId(''); setPermitRevId('')
    setRpImageCheck(null)
    setSheetGateCheck(null); setSheetGateMsg('')
    window.dispatchEvent(new CustomEvent('post-ms-modal-closed', { detail: { msId: closingMsId, posted } }))
  }

  function openAddReview() {
    setAddReview(a => ({ ...a, open: true, permitId: a.permitId || ((permitCtx && permitCtx.permits && permitCtx.permits[0]) ? permitCtx.permits[0].id : 'new') }))
    if (orgs === null) {
      if (ctx.orgsCache && ctx.orgsCache.length) setOrgs(ctx.orgsCache)
      else getOrganizations().then(list => { ctx.orgsCache = list; setOrgs(list) }).catch(() => setOrgs([]))
    }
  }
  /** Create the agency review (and the permit, if new) right in the post window, then re-read what
   *  this post logs with the new review ticked. Linked to this milestone so the chain finds it. */
  async function doAddReview() {
    const a = addReview
    if (!a.orgId) { setError('Pick the agency.'); return }
    if (a.permitId === 'new' && !a.newType) { setError('Pick the kind of permit.'); return }
    setAddBusy(true); setError('')
    try {
      let permitId = a.permitId
      if (permitId === 'new') {
        const p = await createPermit(permitCtx.projectId || msProjectId, { typeOfPermit: a.newType })
        if (p && p.error) throw new Error(typeof p.error === 'string' ? p.error : (p.error.message || 'permit not created'))
        permitId = p && p.id
      }
      if (!permitId) throw new Error('The permit was not created.')
      const r = await createAgencyReview(permitId, { agencyOrgId: a.orgId, milestoneIds: [msId] })
      if (r && r.error) throw new Error(typeof r.error === 'string' ? r.error : (r.error.message || 'agency review not created'))
      const c = await getPostingPermitContext(msId, date || todayStr())
      setPermitCtx(c || { kind: null })
      if (r && r.id) setPermitPick(p => ({ ...p, [r.id]: true }))
      setAddReview({ open: false, permitId: '', newType: '', orgId: '' })
      ctx.permitReviewCache = undefined; ctx.permitCache = undefined
    } catch (e) { setError('Could not add the agency review: ' + (e.message || String(e))) }
    finally { setAddBusy(false) }
  }

  /** What this post logs in Permits, or null. The type, date and next date come from the posting
   *  itself (locked in the window); only the reviews and the memo are the poster's to choose. */
  function permitPlan() {
    const c = permitCtx
    if (!c || !c.kind || c.needsIntake) return null
    const already = new Set((c.alreadyLogged || []).map(a => a.reviewId))
    const targets = (c.reviews || []).filter(r => permitPick[r.id] && !already.has(r.id))
    const sends = c.kind === 'submitted' || c.kind === 'resubmitted'
    const h = sends && handoffs && handoffs.length ? handoffDates[handoffs[0].milestoneId] || {} : null
    return {
      kind: c.kind, activityType: c.activityType, plannedNext: c.plannedNext,
      actDate: c.date || date || todayStr(),
      nextDate: h ? (h.expectedOn || '') : permitNextDate,
      nextFromHandoff: !!h, expectedSource: h ? (h.source || 'estimate') : null,
      targets, reviewsExist: (c.reviews || []).length > 0, alreadyCount: already.size,
    }
  }

  async function handleConfirm() {
    if (saving || !pickersReady || handoffs === null || permitCtx === null) return
    // Passengers (2026-09-09): re-checked live at the moment of posting.
    try {
      const pr = await getPassengersFor([msId])
      const aboard = (pr.byMilestone || {})[msId] || []
      if (aboard.length) { setPassengers(aboard); setPassengerGate('blocked'); return }
    } catch (_) { /* fail open */ }

    let valid = true
    if (isPermit && !agencyId) {
      setAgencyInvalid(true); setTimeout(() => setAgencyInvalid(false), 2000); valid = false
    }
    if (isRevision) {
      // Each revision milestone requires the ONE matching Delta Revision link — Permit Revision
      // for the permit-flavored type, Plan Check Revision for the rest (PPC Drawings Plan Check
      // Revision / DAPC Drawings Revisions / Plan Check Resubmission). Only the matching picker is
      // ever shown (see the JSX), so only that one is validated here.
      if (isPermitRevisionMs) {
        if (!permitRevId) { setPermitRevInvalid(true); setTimeout(() => setPermitRevInvalid(false), 2000); valid = false }
      } else {
        if (!planCheckId) { setPlanCheckInvalid(true); setTimeout(() => setPlanCheckInvalid(false), 2000); valid = false }
      }
    }
    if (!valid) return
    const missingExpected = (handoffs || []).filter(h => !(handoffDates[h.milestoneId] || {}).expectedOn)
    if (missingExpected.length) {
      setError('Say when it\'s expected back, even as a guess.')
      return
    }
    const pp = permitPlan()
    if (pp && !pp.reviewsExist) {
      setError('Add the agency review first. Posting this logs the activity on it.')
      return
    }
    if (pp && pp.reviewsExist && !pp.targets.length && !pp.alreadyCount) {
      setError('Pick the agency review this goes to, so the permit activity is logged.')
      return
    }
    if (pp && pp.targets.length && !permitMemo.trim()) {
      setError('Write the memo for the permit activity. The client sees it.')
      return
    }
    // The client gets this update, worded exactly as written — show it and have the poster confirm.
    if (pp && pp.targets.length) {
      let reachInfo = null
      try { reachInfo = await getClientReach({ milestoneId: msId, activityType: pp.activityType }) }
      catch { reachInfo = { hasJourney: true, subscribers: 0, held: false, clientLabel: activityPlain(pp.activityType) } }   // can't tell: warn anyway
      const ok = await confirmClientUpdate({
        reach: reachInfo, memo: permitMemo, silent: permitSend === 'silent',
        confirmLabel: permitSend === 'silent' ? 'Post and update silently' : 'Post and send to client',
        agencies: pp.targets.map(r => r.agency).filter(Boolean),
      })
      if (!ok) return
    }

    setSaving(true); setError('')

    // Drawing Sheet ids this milestone touches — captured in the gate check below (no extra
    // fetch) so the post-success block can invalidate their Reference/Production cache entries.
    // See the 'sheet-revisions-stale' dispatch near the end of this function.
    let postedSheetIds = null

    // Gate: a milestone can't be posted until every checklist item is completed or marked not required.
    // QA/QC-category items are excluded — they're hidden from the checklist branch entirely
    // (migrating to AlderQC) so there's no way for anyone to complete or skip them here.
    try {
      const items = await getMilestoneChecklistItems(msId)
      const gated = (items || []).filter(function (it) {
        const cat = getFirst((it.fields || {})['Category (from Deliverable Checklist Item Template)']) || 'Other'
        return HIDDEN_CL_CATEGORIES.indexOf(cat) === -1
      })
      const open = gated.filter(function (it) { const cf = it.fields || {}; return !cf['Complete'] && !cf['Milestone Not Required'] })
      if (open.length) {
        setSaving(false)
        setOpenCl(open.map(it => ({ id: it.id, name: getFirst((it.fields || {})['Name for List (from Deliverable Checklist Item Template)']) || getFirst((it.fields || {})['Name']) || 'Checklist item' })))
        setError('')
        return
      }
      setOpenCl(null)
    } catch (e) { /* couldn't load the checklist (transient) — don't hard-block the post */ }

    // Gate: ANY milestone with sheet revisions attached requires them all QC-approved before
    // posting — not just the curated SHEET_GATE_MS_TYPES types (see the matching comment on the
    // open-effect's check above; 2026-08). Only the curated types additionally require at least
    // one sheet revision to exist at all, since that's their whole purpose.
    {
      let sheetRevs = null
      try { sheetRevs = await getMilestoneSheetRevisions(msId) } catch {
        setSaving(false)
        setError('Could not verify drawing sheets — please try again.')
        return
      }
      if (!sheetRevs || sheetRevs.length === 0) {
        if (isSheetGateMilestone(delTypeId)) {
          setSaving(false)
          setError('Drawing sheets must be added to this milestone before it can be posted.')
          return
        }
      } else {
        postedSheetIds = [...new Set(sheetRevs.map(r => getFirst(r.fields['Drawing Sheet'])).filter(Boolean))]
        // Authoritative QC state lives in Supabase qc_versions — the same source the outline's "QC ✓"
        // badge reads. The Airtable 'QC Approved' field is a best-effort mirror that can lag (or never
        // land), so gate on Supabase. This keeps "what you see (QC ✓) = what posting enforces."
        let qcSum = {}
        try {
          // Race a timeout so a slow/hung QC request can never leave the modal stuck on "Posting…".
          qcSum = await Promise.race([
            getRevisionQcSummaries(sheetRevs.map(r => r.id)),
            new Promise((_, rej) => setTimeout(() => rej(new Error('qc-timeout')), 12000)),
          ])
        } catch { setSaving(false); setError('Could not verify QC status — please try again.'); return }
        const unapproved = sheetRevs.filter(r => !(qcSum[r.id] && qcSum[r.id].approved))
        if (unapproved.length) {
          setSaving(false)
          setError(unapproved.length + ' drawing sheet' + (unapproved.length === 1 ? '' : 's') + ' must be cleared through QC before this milestone can be posted.')
          return
        }
      }
    }

    // Gate: selections milestones (docs/SELECTIONS_PACKAGE_DOCUMENTS.md) — every Documents row
    // (Outline Specifications / Schedules) must be marked complete before posting (Matt, 2026-09-24:
    // "an important thing to complete before we post so we don't miss anything").
    const selKinds = selectionsDocKinds(delTypeId)
    if (selKinds.length) {
      let missing
      try { missing = await incompleteSelectionsDocs(msId, selKinds) }
      catch { setSaving(false); setError('Could not check the Documents rows — please try again.'); return }
      if (missing.length) {
        const names = missing.map(k => SELECTIONS_DOC_LABELS[k] || k)
        setSaving(false)
        setError(names.join(' and ') + (names.length === 1 ? ' has' : ' have') + ' to be marked complete under Documents before this milestone can be posted.')
        return
      }
      // Every markup note on those review copies must be resolved too (Matt, 2026-10-28), so a
      // flagged problem can't be issued to the client unanswered.
      let open
      try { open = await getOpenNoteCounts(msId, selKinds) }
      catch { setSaving(false); setError('Could not check the markup notes — please try again.'); return }
      const withOpen = selKinds.filter(k => open[k] > 0)
      if (withOpen.length) {
        const parts = withOpen.map(k => (SELECTIONS_DOC_LABELS[k] || k) + ' (' + open[k] + ')')
        setSaving(false)
        setError('Resolve the open markup notes before posting: ' + parts.join(', ') + '. Open the row\'s ⋯ menu → Markup & QC.')
        return
      }
    }

    // Spec- and/or schedule-bearing milestone: confirm the consequence BEFORE posting, so the user
    // understands that posting issues + locks the spec AND the schedule (and who will see them).
    // Posting is the single issue/lock action for both deliverables.
    const specMode = SPEC_RELEASE_MS_TYPE_IDS[delTypeId]
    const scheduleMode = SCHEDULE_RELEASE_MS_TYPE_IDS[delTypeId]
    // The release narrative: drafted from what changed since the last release (the AlderSPEC
    // Changes tab's comparison, limited to this milestone's members), edited by the person here,
    // stored with the release after it issues. docs/ALDERSPEC.md.
    let releaseNarrative = null
    const specConcept = (ctx.conceptCache || []).find(c => c.fields && c.fields['Selected Concept'] === true) || (ctx.conceptCache || [])[0]
    if (specMode || scheduleMode) {
      let specOpen = 0, schedOpen = 0
      if (specMode) {
        try { const mem = await getMilestoneSpecSections(msId); specOpen = (mem || []).filter(m => m.version && m.version.status === 'open').length } catch { specOpen = 0 }
      }
      if (scheduleMode) {
        try { const sm = await getMilestoneScheduleItems(msId); schedOpen = (sm || []).filter(m => m.version && m.version.status === 'open').length } catch { schedOpen = 0 }
      }
      if (specOpen > 0 || schedOpen > 0) {
        const mode = specMode || scheduleMode
        const gcNote = mode === 'outline'
          ? 'This is the client-facing outline release — it is not shown to the contractor.'
          : 'These will be published to the contractor portal.'
        let changes = null
        try {
          changes = await getSpecChanges(msProjectId, specConcept ? specConcept.id : null,
            { kind: (specMode || scheduleMode) === 'outline' ? 'outline' : 'body', milestoneId: msId })
        } catch { changes = null /* the narrative is drafted when it can be; posting never waits on it */ }
        const parts = []
        if (specOpen) parts.push(specOpen + ' spec section' + (specOpen === 1 ? '' : 's'))
        if (schedOpen) parts.push(schedOpen + ' schedule item' + (schedOpen === 1 ? '' : 's'))
        const narrative = await promptDialog({
          title: 'Post and issue?',
          message: 'Posting "' + (ms?.fields?.['Full Deliverable Name'] || ms?.fields?.['Name'] || 'this milestone') + '" will issue and lock ' +
            parts.join(' and ') + ' as versioned snapshots. They become read-only — to revise later, carry forward into a new milestone.\n\n' + gcNote +
            '\n\nWhat changed in this release, drafted from the comparison with the last one. Edit it before posting; it is kept with the release.',
          defaultValue: (changes && changes.drafted) || '',
          multiline: true,
          confirmLabel: 'Post & issue',
        })
        if (narrative === null) { setSaving(false); return }
        releaseNarrative = { text: narrative, changes }
      }
    }

    if (approval.meetingId && !String(approval.quote || '').trim()) {
      setSaving(false); setError('Add the line in the minutes that approved it, or pick “Not from a meeting”.'); return
    }
    try {
      const res = await postMilestone(msId, {
        date:         date || todayStr(),
        agencyId:     isPermit   ? agencyId    : undefined,
        planCheckId:  isRevision ? planCheckId : undefined,
        permitRevId:  isRevision ? permitRevId : undefined,
      })
      if (res.error) throw new Error(res.error)
      // The approving meeting behind a Client Review (docs/COMPRESSED_CHAIN.md). The post already
      // happened; a failure here is said, never silent.
      if (approval.meetingId) {
        const m = ctx.currentMember
        try {
          await recordPost({ milestoneId: msId, meetingId: approval.meetingId, meetingLabel: approval.meetingLabel, quote: String(approval.quote || '').trim(),
            note: chainNote || undefined, memberId: wf.currentMemberId, memberName: (m && m.fields && (m.fields['Member Name'] || m.fields['Name'])) || '' })
        } catch (e) { alertDialog({ title: 'Posted, but the meeting was not recorded', message: String(e.message || e) }) }
      }
      /* Update in-memory */
      if (ms) {
        ms.fields['Posted Date'] = date || todayStr()
        if (isPermit   && agencyId)    ms.fields['Agency Approvals']            = [agencyId]
        if (isRevision && planCheckId) ms.fields['Delta Revisions - Plan Check'] = [planCheckId]
        if (isRevision && permitRevId) ms.fields['Delta Revisions - Permit']     = [permitRevId]
      }
      /* Schedule sync: only when the posted date differs from the LFT and the user chose to update the
         schedule. Moves the finish to the posted date and re-flows downstream (the same cascade the
         Gantt/nightly use), Supabase master + Airtable mirror. Replaces the retired sync-checkbox. */
      /* Posting sent the feeders after it: mark each sent on the posted date with its expected date.
         The post already succeeded; a failure here is surfaced, never silent. */
      let handoffNote = null
      if (handoffs && handoffs.length) {
        try {
          const m = ctx.currentMember
          await markFeedersSent(msId, date || todayStr(), handoffs.map(h => ({
            milestoneId: h.milestoneId,
            expectedOn: handoffDates[h.milestoneId].expectedOn,
            expectedSource: handoffDates[h.milestoneId].source || 'estimate',
          })), m?.fields?.['Member Name'] || m?.fields?.['Name'] || null)
          if (ctx.feederCache) handoffs.forEach(h => { delete ctx.feederCache[h.milestoneId] })
        } catch (e) { handoffNote = { ok: false, error: e.message || String(e) } }
      }
      /* Posting logs the permit activity on the chosen agency review(s) — the entry the Permit
         Coordinator would otherwise make by hand in Permits. Then the client's Permitting card
         updates and subscribers are emailed (server side). Surfaced on failure, never silent. */
      let permitNote = null
      if (pp && pp.targets.length) {
        try {
          const ids = []
          for (const r of pp.targets) {
            const rec = await logPermitActivity(r.id, pp.activityType, {
              date: pp.actDate, remarks: permitMemo.trim(), recordedByMemberId: wf.currentMemberId || undefined,
              plannedNext: pp.plannedNext || undefined, plannedNextDate: (pp.plannedNext && pp.nextDate) ? pp.nextDate : undefined,
              permitId: r.permitId || undefined,
            })
            if (rec && rec.id) ids.push(rec.id)
          }
          if (ids.length) {
            const m = ctx.currentMember
            await reportActivitiesLogged({ activityIds: ids, source: 'milestone_post', milestoneId: msId, expectedSource: pp.expectedSource, silent: permitSend === 'silent',
              linkReviewIds: pp.targets.map(r => r.id), memberName: m?.fields?.['Member Name'] || m?.fields?.['Name'] || null })
          }
          if (ctx.permitActivityCache) pp.targets.forEach(r => { ctx.permitActivityCache[r.id] = undefined })
          ctx.permitReviewCache = undefined; ctx.permitCache = undefined
        } catch (e) { permitNote = { ok: false, error: e.message || String(e) } }
      }
      let syncNote = null
      if (lftDiffers && syncChoice === 'sync') {
        try {
          await syncFinishAndCascade({ projectId: msProjectId, msId, newFinish: date || todayStr(), records: ctx.state?.milestones || [] })
        } catch (e) { syncNote = { ok: false, error: e.message || String(e) } }
      }
      /* Posting a spec-bearing milestone IS the act of issuing + locking its spec — one action, no
         separate "Issue" step. Freeze every open section version at this milestone into a snapshot
         (mode per the deliverable type). The post already succeeded; we surface the result rather than
         swallow it, so issuance is verifiable (careful version control). */
      let specNote = null
      if (specMode) {
        try {
          const projId = msProjectId
          const concept = (ctx.conceptCache || []).find(c => c.fields && c.fields['Selected Concept'] === true) || (ctx.conceptCache || [])[0]
          const r = await issueMilestoneSpec(projId, concept ? concept.id : null, {
            id: msId, name: ms?.fields?.['Full Deliverable Name'] || ms?.fields?.['Name'] || '', mode: specMode,
          })
          specNote = { ok: true, issued: (r && r.issued) || 0, mode: specMode }
        } catch (e) { specNote = { ok: false, error: e.message || String(e) } }
      }
      /* Posting also issues the schedule: freeze every open schedule version at this milestone into a
         snapshot (same single-action model as specs). Best-effort; surfaced via msScheduleCache reload. */
      // Keep the narrative with the release (best-effort; the post and the issue already succeeded).
      if (releaseNarrative) {
        const ch = releaseNarrative.changes || {}
        const slim = { sections: (ch.sections || []).map(s => ({ section_number: s.section_number, section_name: s.section_name, status: s.status, phrase: s.phrase || null })),
          schedules: (ch.schedules || []).map(s => ({ schedule_type: s.schedule_type, label: s.label, status: s.status, fields: s.fields })) }
        saveSpecReleaseNotes({ projectId: msProjectId, conceptAirtableId: specConcept ? specConcept.id : null, milestoneId: msId,
          milestoneName: ms?.fields?.['Full Deliverable Name'] || ms?.fields?.['Name'] || '',
          baselineMilestoneId: ch.baseline ? ch.baseline.milestoneId : null, baselineName: ch.baseline ? ch.baseline.name : null,
          drafted: ch.drafted || null, narrative: releaseNarrative.text, changes: slim }).catch(() => {})
      }
      let scheduleIssueError = null
      if (scheduleMode) {
        try {
          const projId = msProjectId
          await issueMilestoneSchedule(projId, {
            id: msId, name: ms?.fields?.['Full Deliverable Name'] || ms?.fields?.['Name'] || '', mode: scheduleMode,
          })
          if (ctx.msScheduleCache) ctx.msScheduleCache[msId] = undefined
          window.dispatchEvent(new CustomEvent('schedule-membership-changed'))
        } catch (e) { scheduleIssueError = e.message || String(e) /* the post already succeeded */ }
      }
      /* Selections milestones: re-print each Documents row from what was just ISSUED (no watermark,
         dated), replacing the review copy — overrides the lock, posting is the terminal issue. Must run
         after the issue steps above; a row whose issue failed is not printed (it would print live). */
      let selectionsNote = null
      if (selKinds.length) {
        const blocked = {}
        if (specNote && !specNote.ok) blocked['outline-spec'] = true
        if (scheduleIssueError) blocked.schedules = true
        const toPrint = selKinds.filter(k => !blocked[k])
        const errs = selKinds.filter(k => blocked[k]).map(k => (SELECTIONS_DOC_LABELS[k] || k) + ': not issued, so not re-printed.')
        if (toPrint.length) {
          try {
            const m = ctx.currentMember
            const r = await issueSelectionsDocs({
              milestoneId: msId, projectId: msProjectId, kinds: toPrint, issueDate: date || todayStr(),
              projectName: getFirst(ms?.fields?.['Project Name']) || ctx.projectName || '',
              actorName: m?.fields?.['Member Name'] || m?.fields?.['Name'] || '',
            })
            for (const [k, v] of Object.entries(r.results || {})) if (!v.ok) errs.push((SELECTIONS_DOC_LABELS[k] || k) + ': ' + v.error)
          } catch (e) { errs.push(e.message || String(e)) }
        }
        if (errs.length) selectionsNote = { ok: false, error: errs.join('\n') }
        if (ctx.docCache) ctx.docCache[msId] = undefined
      }
      /* Pre-design Research milestone: posting freezes the issued report as the milestone's final PDF
         (server renders + stores it under the milestone's documents). Best-effort; surfaced on failure. */
      let predesignNote = null
      let opcNote = null
      if (delTypeId === PRE_DESIGN_RESEARCH_TYPE_ID) {
        const projId = msProjectId
        try {
          const r = await freezePreDesignReport(projId, msId)
          predesignNote = (r && r.ok) ? { ok: true } : { ok: false, error: (r && r.error) || 'Unknown error' }
        } catch (e) { predesignNote = { ok: false, error: e.message || String(e) } }
        // Also freeze the Owner's Project Criteria report onto this milestone (gated to this post),
        // so it surfaces in the Client Hub alongside the research report. Best-effort.
        try {
          const r = await freezeOpcReport(projId, msId)
          opcNote = (r && r.ok) ? { ok: true } : { ok: false, error: (r && r.error) || 'Unknown error' }
        } catch (e) { opcNote = { ok: false, error: e.message || String(e) } }
      }
      /* Site Analysis: publish the milestone's most-recently-saved analysis PDF into Documents
         (source 'site-analysis') — Pre-design Research only. The tool can be launched from other
         milestones' Documents menu to edit the shared project map, but the frozen snapshot should
         only ever land (and surface in the Client Hub) on Pre-design Research. Fire-and-forget:
         no analysis yet means freeze no-ops silently, so this never surfaces a failure dialog. */
      if (delTypeId === PRE_DESIGN_RESEARCH_TYPE_ID) {
        try { await freezeSiteAnalysis(msProjectId, msId) } catch (_) { /* best-effort */ }
      }
      /* Field Observation (Construction Gates Phase B): publish the ISSUED observation's PDF into
         Documents (source 'field-observation') on every post of a gate-review/Field Report
         milestone. No-ops silently if no observation was ever opened, or it's still a draft (never
         studio-reviewed) — an unreviewed field walk should never silently become the official
         record. Fire-and-forget, same contract as the site-analysis freeze above. */
      if (FIELD_OBSERVATION_MS_TYPE_IDS.includes(delTypeId)) {
        try { await freezeFieldObservation(msProjectId, msId) } catch (_) { /* best-effort */ }
      }
      /* Titleblock issuance: burn our synced issuance block onto the milestone's owned sheets
         (no-ops server-side for milestones with no owned drawing revisions or no calibrated
         profile). Best-effort — surfaced on failure so the released set isn't silently un-synced. */
      let issuanceNote = null
      try {
        const r = await burnMilestoneIssuance(msId)
        issuanceNote = { ok: true, burned: (r && r.burned) || 0 }
      } catch (e) { issuanceNote = { ok: false, error: e.message || String(e) } }
      /* Reference/Production > Drawing Sheets doesn't refresh on its own after a post — it reads
         ctx.sheetRevCache, a lazy-load-once cache with no invalidation hook, so posting (which
         changes each sheet's Posted Date, revision Type, issued/stamped PDF, etc.) left that view
         showing pre-post data until a hard page reload (Matt, 2026-07-23). Invalidate the cache for
         every sheet this milestone touches and tell any already-mounted rows to refetch immediately
         — see the 'sheet-revisions-stale' listener in DrawingSheetList.jsx's SheetRow (production
         context only; it fires unconditionally, not gated on expanded state, since even the
         collapsed row's posted/in-progress summary badge reads this same cache live).
         msId is included so MilestoneSections.jsx's own listener can refresh ctx.sheetCache[msId]
         too — the milestone-context Drawing Sheets section (where the QC "Replace (Admin)"
         button's posted-state check lives) reads a completely separate cache and was going stale
         the same way, requiring a hard refresh to see the button re-enable after unpost (Matt,
         2026-07-23). */
      if (postedSheetIds && postedSheetIds.length) {
        if (ctx.sheetRevCache) postedSheetIds.forEach(id => { ctx.sheetRevCache[id] = undefined })
        window.dispatchEvent(new CustomEvent('sheet-revisions-stale', { detail: { sheetIds: postedSheetIds, msId } }))
      }
      /* Fire-and-forget: tell the contractor-portal to email subscribers if this posted
         milestone carries drawing sheets (the endpoint no-ops otherwise). */
      try {
        const projId = msProjectId
        if (projId) {
          const tok = typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken().catch(() => null) : null
          fetch('/api/contractor-notify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
            body: JSON.stringify({ event: 'drawings', projectId: projId, milestoneId: msId, milestoneName: ms?.fields?.['Full Deliverable Name'] || ms?.fields?.['Name'] || '' }),
          }).catch(() => {})
        }
      } catch (_) { /* non-fatal */ }
      /* Optionally add this freshly-posted milestone to the Client Hub (draft or final). A final
         entry is published immediately (visible to the client) and the server notifies subscribers;
         a draft is saved unpublished for the team to review first. Best-effort; surfaced on failure. */
      let journeyNote = null
      if (journeyEligible && journeyChoice !== 'none') {
        try {
          const projId = msProjectId
          const projName = getFirst(ms?.fields?.['Project Name']) || ''
          const { journey } = await getOrCreateLink(projId, projName)
          await upsertEntry({
            journeyId: journey.id,
            type: 'milestone',
            title: null,
            data: {},
            milestoneAirtableId: msId,
            milestoneType: delType,
            milestoneTypeId: delTypeId,
            published: journeyChoice === 'final' || journeyChoice === 'silent',
            silent: journeyChoice === 'silent',
          })
          // PDF documents filed under this milestone that are still drafts go out with it
          // (docs/CLIENT_HUB_PDF_DOCUMENTS.md). Sketch ribbons keep their own publish step.
          if (journeyChoice === 'final' || journeyChoice === 'silent') {
            const { entries } = await listEntries(journey.id)
            const drafts = (entries || []).filter(e => e.type === 'document' && !e.published && e.data && e.data.milestoneId === msId && e.data.pdf && e.data.pdf.url)
            for (const d of drafts) await upsertEntry({ id: d.id, published: true, silent: journeyChoice === 'silent' })
          }
          window.dispatchEvent(new CustomEvent('journey-updated', { detail: { journeyId: journey.id } }))
        } catch (e) { journeyNote = { ok: false, error: e.message || String(e) } }
      }
      postedRef.current = true   // close() reads this to report a real post vs an abandoned one
      close()
      window.dispatchEvent(new CustomEvent('outline-render'))
      // The visit was also a construction review: its own post window opens next, same date, its own checks.
      if (alsoReview) { const d = date || todayStr(); setTimeout(() => window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId: alsoReview, date: d, instruction: 'This review was the visit in the Field Report you just posted.' } })), 400) }
      // The user confirmed the issue/lock before posting, so success is silent. Only surface a failure
      // (only knowable after the attempt) so they know the spec did NOT actually freeze.
      if (specNote && !specNote.ok) {
        alertDialog({ title: 'Posted — but the spec did NOT lock', message: 'The milestone posted, but issuing its spec failed:\n\n' + specNote.error + '\n\nThe spec is NOT frozen yet. Unpost and post again to retry, or open AlderSPEC.' })
      }
      if (selectionsNote && !selectionsNote.ok) {
        alertDialog({ title: 'Posted — but the documents did NOT lock', message: 'The milestone posted, but printing the issued documents failed:\n\n' + selectionsNote.error + '\n\nThe issued PDFs were not saved. Unpost and post again to retry.' })
      }
      if (predesignNote && !predesignNote.ok) {
        alertDialog({ title: 'Posted — but the report did NOT lock', message: 'The milestone posted, but freezing the pre-design report failed:\n\n' + predesignNote.error + '\n\nThe final PDF was not saved. Unpost and post again to retry.' })
      }
      if (opcNote && !opcNote.ok) {
        alertDialog({ title: 'Posted — but the criteria report did NOT lock', message: "The milestone posted, but freezing the Owner's Project Criteria report failed:\n\n" + opcNote.error + '\n\nUnpost and post again to retry.' })
      }
      if (journeyNote && !journeyNote.ok) {
        alertDialog({ title: 'Posted — but not added to the Client Hub', message: 'The milestone posted, but adding it to the Client Hub failed:\n\n' + journeyNote.error + '\n\nYou can add it from the Client Hub branch (⋯ → Add entry).' })
      }
      if (issuanceNote && !issuanceNote.ok) {
        alertDialog({ title: 'Posted — but titleblocks did NOT sync', message: 'The milestone posted, but stamping the issuance block onto the drawing sheets failed:\n\n' + issuanceNote.error + '\n\nThe released PDFs show their original titleblocks. Unpost and post again to retry.' })
      }
      if (permitNote && !permitNote.ok) {
        alertDialog({ title: 'Posted — but the permit activity was NOT logged', message: 'The milestone posted, but logging it in Permits failed:\n\n' + permitNote.error + '\n\nLog it by hand on the agency review in Permits.' })
      }
      if (handoffNote && !handoffNote.ok) {
        alertDialog({ title: 'Posted — but not marked sent', message: 'The milestone posted, but marking what it sent failed:\n\n' + handoffNote.error + '\n\nOpen the feeder after this milestone and use "Mark it submitted…" to record it.' })
      }
      if (syncNote && !syncNote.ok) {
        alertDialog({ title: 'Posted — but the schedule did NOT update', message: 'The milestone posted, but moving the finish and re-flowing downstream failed:\n\n' + syncNote.error + '\n\nThe posted date saved; the schedule is unchanged. Open the Gantt scheduler to adjust manually.' })
      }
    } catch {
      setSaving(false)
      setError('Post failed — please try again.')
    }
  }

  if (!open) return null

  const btnBase = {
    border: 'none', borderRadius: 4, padding: '8px 18px',
    fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500,
    cursor: 'pointer', transition: 'opacity 0.1s'
  }

  // Waiting on a predecessor: it cannot be posted (Matt, 2026-09-24; outline/blocked.js). Every
  // post path opens this window, so this is the one place the refusal lives.
  const waitingOn = ms ? blockedBy(ms) : []
  if (waitingOn.length && earlierSteps(ms).length) {
    return createPortal(
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: zTop || 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div data-modal-id="PostMilestoneModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', padding: '22px 24px', width: 520, maxWidth: '100%', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
          <PostChainPanel ms={ms} projectId={msProjectId} date={date} setDate={setDate} btnBase={btnBase} onClose={close}
            onPosted={({ approval: a, note }) => { if (a) setApproval(a); setChainNote(note || ''); setChainNote(note || ''); setChainTick(t => t + 1) }} />
        </div>
      </div>,
      document.body
    )
  }
  if (waitingOn.length) {
    const names = waitingOn.map(msLabel)
    return createPortal(
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: zTop || 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div data-modal-id="PostMilestoneModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', padding: '22px 24px', width: 400, maxWidth: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 12 }}>
            Not yet
          </div>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', marginBottom: 20, lineHeight: 1.5 }}>
            {msName} is waiting on {names.length === 1 ? names[0] : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]}.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={close} style={{ ...btnBase, background: '#07272D', color: '#fff' }}>Close</button>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        zIndex: zTop || 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
      }}
    >
      <div data-modal-id="PostMilestoneModal" style={{
        background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)',
        padding: '22px 24px', width: passengerGate === 'blocked' ? 620 : 400, maxWidth: '100%',
        maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)'
      }}>

        {/* Milestone not in the outline's cache and the fallback fetch hasn't resolved yet, or
            failed outright — show something rather than an empty shell (see fallbackMs's own
            comment). Everything below this assumes `ms` is real. */}
        {!ms && fallbackMs !== 'error' && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, color: 'var(--aj-text-muted)' }}>Loading milestone…</div>
          </div>
        )}
        {!ms && fallbackMs === 'error' && (
          <>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 12 }}>
              Post milestone
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: '#c0392b', marginBottom: 20, lineHeight: 1.5 }}>
              Could not load this milestone. Close and try again.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={close} style={{ ...btnBase, background: '#07272D', color: '#fff' }}>Close</button>
            </div>
          </>
        )}

        {/* Rendering Package gate — checking */}
        {ms && isRenderingPkg && rpImageCheck === 'checking' && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, color: 'var(--aj-text-muted)' }}>Checking images…</div>
          </div>
        )}

        {/* Rendering Package gate — no images uploaded */}
        {isRenderingPkg && rpImageCheck === 'no-images' && (
          <>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 12 }}>
              Post milestone
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', fontWeight: 500, padding: '8px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4, marginBottom: 16 }}>
              {msName}
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: '#c0392b', marginBottom: 20, lineHeight: 1.5 }}>
              At least one rendering must be uploaded to this milestone before it can be posted. Open the Documents section and add renderings, then try again.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={close} style={{ ...btnBase, background: '#07272D', color: '#fff' }}>Close</button>
            </div>
          </>
        )}

        {/* Drawing-set gate — checking */}
        {(!isRenderingPkg || rpImageCheck === 'has-images') && sheetGateCheck === 'checking' && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, color: 'var(--aj-text-muted)' }}>Checking drawing sheets…</div>
          </div>
        )}

        {/* Passengers aboard — blocked (docs/PASSENGERS.md) */}
        {passengerGate === 'blocked' && (
          <PassengersBlock msName={msName} msId={msId} passengers={passengers} onClose={close}
            onChanged={async () => {
              try {
                const r = await getPassengersFor([msId])
                const list = (r.byMilestone || {})[msId] || []
                setPassengers(list); setPassengerGate(list.length ? 'blocked' : 'ok')
              } catch (_) { /* keep as is */ }
            }} />
        )}

        {/* Drawing-set gate — blocked (sheets missing or not yet QC-cleared) */}
        {passengerGate !== 'blocked' && (!isRenderingPkg || rpImageCheck === 'has-images') && sheetGateCheck === 'blocked' && (
          <>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 12 }}>
              Post milestone
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', fontWeight: 500, padding: '8px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4, marginBottom: 16 }}>
              {msName}
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: '#c0392b', marginBottom: 20, lineHeight: 1.5 }}>
              {sheetGateMsg}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={close} style={{ ...btnBase, background: '#07272D', color: '#fff' }}>Close</button>
            </div>
          </>
        )}

        {/* Agency Review not recorded as back yet — posting it logs what came back, so the intake
            (with its game-changer question) has to happen first. */}
        {passengerGate !== 'blocked' && sheetGateCheck === 'ok' && permitCtx && permitCtx.needsIntake && (
          <>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 12 }}>
              Post milestone
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', fontWeight: 500, padding: '8px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4, marginBottom: 16 }}>
              {msName}
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', marginBottom: 20, lineHeight: 1.5 }}>
              Record what came back first: open this milestone's Feeder and use "Record that it came back". That asks the
              game-changer question and gives this post the date and outcome it logs in Permits.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={close} style={{ ...btnBase, background: '#07272D', color: '#fff' }}>Close</button>
            </div>
          </>
        )}

        {/* Normal modal content — show for non-RP milestones (or RP with images confirmed) once any drawing-set gate clears */}
        {passengerGate !== 'blocked' && (!isRenderingPkg || rpImageCheck === 'has-images') && sheetGateCheck === 'ok' && !(permitCtx && permitCtx.needsIntake) && <>

        {/* Title */}
        <div style={{
          fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12,
          letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 6
        }}>
          Post milestone
        </div>

        {/* Milestone name callout */}
        <div style={{
          fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)',
          padding: '8px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4,
          fontWeight: 500, marginBottom: 4
        }}>
          {msName}
        </div>

        {/* AlderCAD (bim-plan/28 round 9): a warning, not a block — the model was saved again after
            these sheets were sent, so their PDFs may not show the latest drawings */}
        {staleSends.length > 0 && (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, lineHeight: 1.5, color: 'var(--aj-text)', background: 'rgba(184,100,10,0.08)', border: '0.5px solid #e0b98a', borderRadius: 4, padding: '8px 10px', margin: '10px 0 4px' }}>
            The AlderCAD model has changed since {staleSends.length === 1 ? 'this sheet was' : 'these sheets were'} sent: {staleSends.map((s) => s.sheetNumber).join(', ')}. Their PDFs may be out of date.{' '}
            <a href={`/modeler?conceptId=${encodeURIComponent(staleSends[0].conceptId)}&projectId=${encodeURIComponent(staleSends[0].projectId || '')}&view=sheets`}
              target="_blank" rel="noreferrer" style={{ color: '#b8640a', fontWeight: 500 }}>
              Open AlderCAD to send them again
            </a>
          </div>
        )}

        {chainNote && (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.45, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
            The steps before it are posted. Post {msName} now if it is done too, or close.
          </div>
        )}
        {instr && (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.45, color: '#7A4E12', background: '#FFF4E5', border: '0.5px solid #E8B978', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
            <b style={{ fontWeight: 600 }}>The instruction:</b> {instr}
          </div>
        )}
        {delTypeId === FIELD_REPORT_TYPE_ID && reviewOpts && reviewOpts.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <ModalLabel>This visit was also…</ModalLabel>
            <select value={alsoReview} onChange={e => setAlsoReview(e.target.value)}
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }}>
              <option value="">Just a Field Report</option>
              {reviewOpts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 4 }}>If it was, that review’s post window opens next with the same date.</div>
          </div>
        )}
        {/* Date */}
        <ModalLabel>Posted date</ModalLabel>
        <input
          type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{
            display: 'block', width: '100%', boxSizing: 'border-box', padding: '7px 10px',
            border: '0.5px solid var(--aj-border-mid)', borderRadius: 4,
            fontFamily: '"DM Mono",monospace', fontSize: 13, color: 'var(--aj-text)',
            outline: 'none', background: 'var(--aj-bg-subtle)', marginBottom: 4
          }}
        />
        {delTypeId === CLIENT_REVIEW_TYPE_ID && <MeetingApprovalFields projectId={msProjectId} value={approval} onChange={setApproval} />}

        {/* Permit variant */}
        {isPermit && (
          agencyRecords === null
            ? <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '8px 0' }}>
                Loading agency approvals…
              </div>
            : <>
                <ModalLabel>Agency approval</ModalLabel>
                <LinkedPicker
                  records={agencyRecords} value={agencyId}
                  onChange={setAgencyId} invalid={agencyInvalid}
                />
              </>
        )}

        {/* Revision variant — only the picker matching this milestone's own flavor shows (see
            isPermitRevisionMs above); a plan-check-flavored milestone never offers a Permit
            Revision field it can't actually use, and vice versa. */}
        {isRevision && (
          planCheckRecords === null
            ? <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '8px 0' }}>
                Loading revisions…
              </div>
            : isPermitRevisionMs ? (
                <>
                  <ModalLabel>Permit revision</ModalLabel>
                  <LinkedPicker
                    records={permitRevRecords || []} value={permitRevId}
                    onChange={setPermitRevId} invalid={permitRevInvalid}
                  />
                </>
              ) : (
                <>
                  <ModalLabel>Plan check revision</ModalLabel>
                  <LinkedPicker
                    records={planCheckRecords || []} value={planCheckId}
                    onChange={setPlanCheckId} invalid={planCheckInvalid}
                  />
                </>
              )
        )}

        {/* Schedule sync — only asked when the posted date differs from the scheduled finish (LFT). */}
        {currentLft && lftDiffers && (
          <div style={{ marginTop: 16 }}>
            <ModalLabel>Schedule</ModalLabel>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', lineHeight: 1.5, marginBottom: 8 }}>
              Posted date is <strong>{Math.abs(cascadePreview?.deltaDays ?? 0)} day{Math.abs(cascadePreview?.deltaDays ?? 0) === 1 ? '' : 's'} {(cascadePreview?.deltaDays ?? 0) >= 0 ? 'later than' : 'earlier than'}</strong> the scheduled finish ({fmtMonthDay(currentLft)}).
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.5 }}>
              The posted date becomes its finish{cascadePreview && cascadePreview.downstream.length ? ', and what follows moves with it.' : '.'} If it was really done earlier than the date above, change the date to when it was done.
            </div>
            {syncChoice === 'sync' && cascadePreview && cascadePreview.downstream.length > 0 && (
              <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 6 }}>
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginBottom: 4 }}>Downstream impact</div>
                {cascadePreview.downstream.slice(0, 5).map(d => (
                  <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', padding: '1px 0' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: d.deltaDays > 0 ? '#b8540a' : '#1D9E75', flexShrink: 0 }}>{d.deltaDays > 0 ? '+' : ''}{d.deltaDays}d → {fmtMonthDay(d.latestFinish)}</span>
                  </div>
                ))}
                {cascadePreview.downstream.length > 5 && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>+{cascadePreview.downstream.length - 5} more</div>}
              </div>
            )}
          </div>
        )}
        {currentLft && !lftDiffers && (
          <div style={{ marginTop: 12, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)' }}>Posted date matches the scheduled finish — schedule unchanged.</div>
        )}

        {/* Feeders this post sends — each is marked sent on the posted date (2026-09-23) */}
        {handoffs && handoffs.map(h => {
          const H = FEEDER_HANDOFF[h.owedBy] || { party: 'them', postsAs: 'Posting this sends it' }
          const v = handoffDates[h.milestoneId] || { expectedOn: '', source: 'estimate' }
          const set = (patch) => setHandoffDates(d => ({ ...d, [h.milestoneId]: { ...v, ...patch } }))
          return (
            <div key={h.milestoneId} style={{ marginTop: 16 }}>
              <ModalLabel>{H.postsAs}</ModalLabel>
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', lineHeight: 1.5, marginBottom: 8 }}>
                <strong>{h.name}{h.subtitle && !String(h.name || '').includes(h.subtitle) ? ' - ' + h.subtitle : ''}</strong> will be marked {H.verb || 'sent'} on the posted date.
                {h.coordinator ? ` ${h.coordinator} chases it from there.` : ''}
              </div>
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', marginBottom: 4 }}>
                When is {H.party} expected to get back to us?
              </div>
              <input type="date" value={v.expectedOn} onChange={e => set({ expectedOn: e.target.value })}
                style={{
                  display: 'block', width: '100%', boxSizing: 'border-box', padding: '7px 10px',
                  border: '0.5px solid ' + (error && !v.expectedOn ? '#c0392b' : 'var(--aj-border-mid)'), borderRadius: 4,
                  fontFamily: '"DM Mono",monospace', fontSize: 13, color: 'var(--aj-text)',
                  outline: 'none', background: 'var(--aj-bg-subtle)', marginBottom: 4,
                }} />
              <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 11.5, color: v.expectedOn ? 'var(--aj-text)' : 'var(--aj-text-muted)' }}>
                {v.expectedOn ? daysOutLabel(v.expectedOn) : 'Required, even as a guess. You will be asked again if it passes.'}
              </div>
              <ExpectedSourcePick owedBy={h.owedBy} value={v.source} onChange={src => set({ source: src })} name={'src-' + h.milestoneId}
                style={{ radio: { display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', cursor: 'pointer' } }} />
            </div>
          )
        })}

        {/* Permit activity this post logs — laid out like the Permits log form; the type, date and next
            date are the posting's own, so they can't disagree (build step 4 slice B, 2026-09-23). */}
        {(() => {
          const pp = permitPlan()
          if (!pp) return null
          const c = permitCtx
          const lock = { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', padding: '6px 9px', background: 'var(--aj-bg-subtle)', border: '0.5px dashed var(--aj-border-mid)', borderRadius: 4 }
          const note = { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 3, lineHeight: 1.45 }
          const alreadyBy = new Map((c.alreadyLogged || []).map(a => [a.reviewId, a]))
          const permits = c.permits || []
          const addForm = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, padding: '9px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4 }}>
              <div>
                <div style={{ ...note, marginTop: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Permit</div>
                <Select value={addReview.permitId} onChange={v => setAddReview(a => ({ ...a, permitId: v }))}
                  options={[...permits.map(p => ({ value: p.id, label: p.type + (p.subName ? ' · ' + p.subName : '') })), { value: 'new', label: 'New permit…' }]}
                  style={{ width: '100%', fontSize: 12 }} />
              </div>
              {addReview.permitId === 'new' && (
                <div>
                  <div style={{ ...note, marginTop: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Kind of permit</div>
                  <Select value={addReview.newType} onChange={v => setAddReview(a => ({ ...a, newType: v }))}
                    options={PERMIT_TYPES.map(t => ({ value: t, label: t }))} placeholder="Select kind…" searchable style={{ width: '100%', fontSize: 12 }} />
                </div>
              )}
              <div>
                <div style={{ ...note, marginTop: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Agency</div>
                {orgs === null
                  ? <div style={note}>Loading agencies…</div>
                  : <Select value={addReview.orgId} onChange={v => setAddReview(a => ({ ...a, orgId: v }))}
                      options={(orgs || []).map(o => ({ value: o.id, label: o.name }))} placeholder="Select agency…" searchable style={{ width: '100%', fontSize: 12 }} />}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {pp.reviewsExist && (
                  <button type="button" onClick={() => setAddReview({ open: false, permitId: '', newType: '', orgId: '' })}
                    style={{ padding: '5px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
                )}
                <button type="button" onClick={doAddReview} disabled={addBusy}
                  style={{ padding: '5px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, cursor: addBusy ? 'default' : 'pointer', opacity: addBusy ? 0.6 : 1 }}>
                  {addBusy ? 'Adding…' : 'Add agency review'}
                </button>
              </div>
            </div>
          )
          return (
            <div style={{ marginTop: 16, padding: '11px 12px', border: '0.5px solid var(--aj-border)', borderRadius: 6 }}>
              <ModalLabel>Logs in Permits</ModalLabel>
              {!pp.reviewsExist ? (
                <>
                  <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', lineHeight: 1.45 }}>
                    This project has no agency review in Permits yet. Posting has to log <strong>{activityPlain(pp.activityType)}</strong> on one, so add it here first.
                  </div>
                  {addReview.open ? addForm : (
                    <button type="button" onClick={openAddReview}
                      style={{ marginTop: 8, padding: '5px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                      Add the agency review
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', marginBottom: 6 }}>
                    Posting logs <strong>{activityPlain(pp.activityType)}</strong> on:
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                    {c.reviews.map(r => {
                      const done = alreadyBy.get(r.id)
                      return (
                        <label key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: done ? 'var(--aj-text-muted)' : 'var(--aj-text)', cursor: done ? 'default' : 'pointer' }}>
                          <input type="checkbox" disabled={!!done} checked={!!done || !!permitPick[r.id]} onChange={e => setPermitPick(p => ({ ...p, [r.id]: e.target.checked }))} style={{ accentColor: '#1D9E75' }} />
                          <span>{r.agency || 'Agency'}{r.permitType ? ' · ' + r.permitType : ''}
                            {done ? <span style={{ color: '#1D9E75' }}> · already logged {fmtMonthDay(done.date)}, won't log again</span> : null}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                  {addReview.open ? <div style={{ marginBottom: 10 }}>{addForm}</div> : (
                    <button type="button" onClick={openAddReview}
                      style={{ marginBottom: 10, padding: 0, background: 'none', border: 'none', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-accent-blue)' }}>
                      + Another agency not listed
                    </button>
                  )}
                  {pp.targets.length > 0 && (
                    <>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ ...note, marginTop: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Date</div>
                          <div style={lock}>{fmtMonthDay(pp.actDate)}</div>
                        </div>
                        {pp.plannedNext && (
                          <div style={{ flex: 2 }}>
                            <div style={{ ...note, marginTop: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Planned next</div>
                            {pp.nextFromHandoff
                              ? <div style={lock}>{activityPlain(pp.plannedNext)}{pp.nextDate ? ' · ' + fmtMonthDay(pp.nextDate) : ' · (expected date above)'}</div>
                              : <div style={{ display: 'flex', gap: 6 }}>
                                  <div style={{ ...lock, flex: 1 }}>{activityPlain(pp.plannedNext)}</div>
                                  <input type="date" value={permitNextDate} onChange={e => setPermitNextDate(e.target.value)} title="Best guess, optional"
                                    style={{ padding: '5px 8px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, fontFamily: '"DM Mono",monospace', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }} />
                                </div>}
                          </div>
                        )}
                      </div>
                      <div style={note}>
                        {pp.kind === 'review'
                          ? 'The date is when it came back, from "Record that it came back".'
                          : 'The date is the posted date' + (pp.nextFromHandoff ? ' and the next date is the expected date above; change them there.' : '; change it above.')}
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <div style={{ ...note, marginTop: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Memo · the client sees this</div>
                        <textarea value={permitMemo} onChange={e => setPermitMemo(e.target.value)} rows={3}
                          placeholder={pp.kind === 'review' ? 'What came back, in a sentence or two for the client.' : 'What went in, in a sentence or two for the client.'}
                          style={{ width: '100%', boxSizing: 'border-box', padding: '7px 9px', border: '0.5px solid ' + (error && !permitMemo.trim() ? '#c0392b' : 'var(--aj-border-mid)'), borderRadius: 4, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', resize: 'vertical' }} />
                        <div style={note}>It goes on the client's Permitting page{permitSend === 'silent' ? '' : ' and in their email update'}. Don't log this again in Permits: it will show there as logged by this post.</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                        {[
                          { v: 'email',  t: 'Update and email', d: 'Shows on the client’s Permitting page. Subscribers get an email.' },
                          { v: 'silent', t: 'Update silently',  d: 'Shows on the client’s Permitting page. No email goes to subscribers.' },
                        ].map(opt => {
                          const active = permitSend === opt.v
                          return (
                            <label key={opt.v} style={{
                              display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer',
                              padding: '8px 10px', borderRadius: 6,
                              border: `0.5px solid ${active ? '#1D9E75' : 'var(--aj-border)'}`,
                              background: active ? 'rgba(29,158,117,0.06)' : 'transparent',
                            }}>
                              <input type="radio" name="permitSend" checked={active} onChange={() => setPermitSend(opt.v)}
                                style={{ marginTop: 2, flexShrink: 0, accentColor: '#1D9E75', cursor: 'pointer' }} />
                              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', lineHeight: 1.4 }}>
                                {opt.t}
                                <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>{opt.d}</div>
                              </div>
                            </label>
                          )
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )
        })()}

        {/* Client Hub — offered only for client-facing deliverable types */}
        {journeyEligible && (
          <div style={{ marginTop: 16 }}>
            <ModalLabel>Client Hub</ModalLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { v: 'none',  t: 'Don’t add',     d: 'Post only — nothing goes to the Client Hub.' },
                { v: 'draft', t: 'Add as draft',  d: 'Saved to the Client Hub unpublished. Your team can preview it; the client can’t see it yet.' },
                { v: 'final', t: 'Add as final',  d: 'Published immediately — visible to the client now, with any PDF documents filed under it. Subscribers get an email that it was added.' },
                { v: 'silent', t: 'Add as final, silently', d: 'Published immediately — visible to the client now, with any PDF documents filed under it. No email goes to subscribers.' },
              ].map(opt => {
                const active = journeyChoice === opt.v
                return (
                  <label key={opt.v} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer',
                    padding: '9px 11px', borderRadius: 6,
                    border: `0.5px solid ${active ? '#1D9E75' : 'var(--aj-border)'}`,
                    background: active ? 'rgba(29,158,117,0.06)' : 'transparent',
                  }}>
                    <input type="radio" name="journeyChoice" checked={active}
                      onChange={() => setJourneyChoice(opt.v)}
                      style={{ marginTop: 2, flexShrink: 0, accentColor: '#1D9E75', cursor: 'pointer' }} />
                    <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', lineHeight: 1.4 }}>
                      {opt.t}
                      <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>{opt.d}</div>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>
        )}

        {openCl && openCl.length > 0 && (
          <div style={{ marginTop: 14, padding: '10px 12px', border: '0.5px solid #e0b4a8', borderRadius: 8, background: 'rgba(192,57,43,0.05)' }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 6 }}>
              {openCl.length} checklist item{openCl.length === 1 ? ' is' : 's are'} still open. Clear {openCl.length === 1 ? 'it' : 'them'} here, then post.
            </div>
            {openCl.map(it => (
              <div key={it.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderTop: '0.5px solid var(--aj-border)' }}>
                <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', lineHeight: 1.4 }}>{it.name}</span>
                <button disabled={it.busy} style={{ ...btnBase, padding: '4px 10px', fontSize: 12, background: '#1D9E75', color: '#fff' }} onClick={async () => {
                  setOpenCl(l => l.map(x => x.id === it.id ? { ...x, busy: true } : x))
                  try { await completeChecklistItem(it.id, true, wf.currentMemberId); setOpenCl(l => l.filter(x => x.id !== it.id)) }
                  catch (e) { setOpenCl(l => l.map(x => x.id === it.id ? { ...x, busy: false } : x)); setError(String(e && e.message || e)) }
                }}>Done</button>
                <button disabled={it.busy} style={{ ...btnBase, padding: '4px 10px', fontSize: 12, background: 'transparent', border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)' }} onClick={async () => {
                  const reason = await promptDialog({ title: 'Not required?', message: it.name, placeholder: 'Why it isn’t needed here', multiline: true, required: true, confirmLabel: 'Mark not required' })
                  if (reason == null) return
                  setOpenCl(l => l.map(x => x.id === it.id ? { ...x, busy: true } : x))
                  try { await markChecklistItemNotRequired(it.id, reason, wf.currentMemberId); setOpenCl(l => l.filter(x => x.id !== it.id)) }
                  catch (e) { setOpenCl(l => l.map(x => x.id === it.id ? { ...x, busy: false } : x)); setError(String(e && e.message || e)) }
                }}>Not required…</button>
              </div>
            ))}
          </div>
        )}
        {openCl && openCl.length === 0 && (
          <div style={{ marginTop: 14, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: '#0f6b48' }}>Checklist cleared. Post when ready.</div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 18 }}>
          {error && (
            <span style={{ fontSize: 12, color: '#c0392b', marginRight: 'auto' }}>{error}</span>
          )}
          <button onClick={close} style={{
            ...btnBase, background: 'transparent',
            border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)'
          }}>
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || !pickersReady || handoffs === null || permitCtx === null}
            style={{
              ...btnBase, background: '#1D9E75', color: 'white',
              opacity: saving || !pickersReady || handoffs === null || permitCtx === null ? 0.6 : 1
            }}
          >
            {saving ? 'Posting…' : ((permitPlan() || {}).targets || []).length ? 'Post and log' : 'Post milestone'}
          </button>
        </div>

        </>}  {/* end normal modal content */}
      </div>
    </div>,
    document.body
  )
}


/**
 * PASSENGERS ABOARD (2026-09-09, docs/PASSENGERS.md). A milestone carrying an open thread cannot post
 * or fast-track. The crew's way out is to close the thread (it rode here to be closed with the work)
 * or to move it to another unposted milestone on the same project at their own discretion (Matt,
 * 2026-10-26: asking the tower held up Armando's Santiago DAPC post). Only the tower (workload/admin
 * seat) sends a passenger back to daily triage.
 */
function PassengersBlock({ msName, msId, passengers, onClose, onChanged }) {
  const isTower = isSeatLoaded() && (hasModule('workload') || hasModule('admin'))
  const [cands, setCands] = useState(null)
  const [pick, setPick] = useState({})
  // Close several at once (AlderFLOW §26): tick the threads this milestone's work answered, one note.
  const [ticked, setTicked] = useState(() => new Set())
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')
  const memberId = wf.currentMemberId
  const memberName = (ctx.currentMember && ctx.currentMember.fields && (ctx.currentMember.fields['Member Name'] || ctx.currentMember.fields['Name'])) || null
  const projectId = (passengers[0] && passengers[0].projectId) || ctx.recordId || null
  const moveOptions = scheduleOrderedOptions(cands, ctx.state && ctx.state.phases, ctx.state && ctx.state.milestones)

  useEffect(() => {
    if (!projectId) return
    getCandidateMilestones(projectId).then(r => setCands(((r.candidates || {})[projectId] || []).filter(c => c.milestoneId !== msId))).catch(() => setCands([]))
  }, [projectId, msId])

  async function run(key, fn) {
    setBusy(key); setErr('')
    try { await fn(); await onChanged() } catch (e) { setErr(String(e && e.message || e)) } finally { setBusy('') }
  }
  const font = { fontFamily: '"DM Sans",sans-serif' }
  return (
    <>
      <div style={{ ...font, fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 12 }}>Post milestone</div>
      <div style={{ ...font, fontSize: 13, color: 'var(--aj-text)', fontWeight: 500, padding: '8px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4, marginBottom: 16 }}>{msName}</div>
      <div style={{ ...font, fontSize: 13, color: '#c0392b', marginBottom: 12, lineHeight: 1.5 }}>
        {passengers.length === 1 ? 'One thread is' : `${passengers.length} threads are`} still riding this milestone. A plane does not land with passengers aboard: close {passengers.length === 1 ? 'it' : 'them'} as part of the work, or move {passengers.length === 1 ? 'it' : 'any that belong later'} to a milestone that hasn't posted yet.
      </div>
      {passengers.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <button style={{ ...font, fontSize: 12, padding: '6px 11px', borderRadius: 4, border: 'none', background: ticked.size ? '#07272D' : 'var(--aj-border-mid)', color: '#fff', cursor: ticked.size ? 'pointer' : 'default' }}
            disabled={!ticked.size || !!busy}
            onClick={async () => {
              const list = passengers.filter(p => ticked.has(p.threadId))
              const note = await promptDialog({ title: `Close ${list.length} thread${list.length === 1 ? '' : 's'}?`, message: `${list.map(p => '"' + p.title + '"').join(', ')} will be marked closed and stop riding ${msName}. One note goes on each.`, placeholder: `e.g. Answered in the ${msName}`, multiline: true, confirmLabel: `Close ${list.length} thread${list.length === 1 ? '' : 's'}` })
              if (note === null) return
              await run('bulk', async () => {
                for (const p of list) {
                  const res = await closeThread(p.threadId, memberId || null, note.trim() || `Answered in ${msName}.`)
                  if (res && res.error) throw new Error(p.title + ': ' + res.error)
                }
                setTicked(new Set())
              })
            }}>{busy === 'bulk' ? 'Closing…' : ticked.size ? `Close the ${ticked.size} ticked` : 'Tick the ones this work answered'}</button>
          <button style={{ ...font, fontSize: 12, padding: 0, border: 'none', background: 'none', color: 'var(--aj-accent)', cursor: 'pointer' }}
            onClick={() => setTicked(ticked.size === passengers.length ? new Set() : new Set(passengers.map(p => p.threadId)))}>{ticked.size === passengers.length ? 'Untick all' : 'Tick all'}</button>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {passengers.map(p => (
          <div key={p.threadId} style={{ border: '0.5px solid ' + (ticked.has(p.threadId) ? 'var(--aj-accent)' : 'var(--aj-border)'), borderRadius: 8, padding: '9px 11px', background: 'var(--aj-bg-subtle)' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: passengers.length > 1 ? 'pointer' : 'default' }}>
              {passengers.length > 1 && <input type="checkbox" checked={ticked.has(p.threadId)} style={{ marginTop: 3 }}
                onChange={e => setTicked(prev => { const n = new Set(prev); if (e.target.checked) n.add(p.threadId); else n.delete(p.threadId); return n })} />}
              <span style={{ minWidth: 0 }}>
                <div style={{ ...font, fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{p.title}</div>
                {/* Where it came from (AlderFLOW §26): the meeting, else who opened it. */}
                {p.origin && <div style={{ ...font, fontSize: 12, color: 'var(--aj-text-mid)', marginTop: 2 }}>{p.origin}{p.description ? ': ' + p.description : ''}</div>}
                <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>{p.type || 'Thread'}{p.pairedBy ? ` · put on this milestone by ${p.pairedBy}${p.pairedAt ? ' ' + new Date(p.pairedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}` : ''}{p.note ? ` · "${p.note}"` : ''}</div>
              </span>
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
              {/* Close it right here. The message above says "close it as part of the work"; before
                  2026-10-12 this window gave no way to do that, and a thread with no phase only shows
                  under Reference > Threads, so a drafter could not find it (Wolford, Armando). */}
              <button style={{ ...font, fontSize: 12, padding: '6px 11px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' }} disabled={busy === p.threadId}
                onClick={async () => {
                  const note = await promptDialog({ title: 'Close this thread?', message: `"${p.title}" will be marked closed and will stop riding ${msName}.`, placeholder: 'Closing note (optional)', multiline: true, confirmLabel: 'Close thread' })
                  if (note === null) return
                  await run(p.threadId, async () => {
                    const res = await closeThread(p.threadId, memberId || null, note.trim() || 'Closed at post time.')
                    if (res && res.error) throw new Error(res.error)
                  })
                }}>Close thread</button>
              <>
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <Select value={pick[p.threadId] || ''} onChange={v => setPick(x => ({ ...x, [p.threadId]: v }))} disabled={cands === null}
                      options={moveOptions} searchable
                      placeholder={cands === null ? 'Loading milestones…' : cands.length ? 'Move to a later milestone…' : 'No other open milestone on this project'}
                      style={{ width: '100%', fontSize: 12 }} />
                  </div>
                  <button style={{ ...font, fontSize: 12, padding: '6px 11px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' }} disabled={!pick[p.threadId] || busy === p.threadId}
                    onClick={() => run(p.threadId, async () => { await crewMovePassenger(memberId, memberName, p.threadId, pick[p.threadId], `Moved off ${msName} at post time`); refreshMsPassengers([msId, pick[p.threadId]]) })}>{busy === p.threadId ? 'Moving…' : 'Move'}</button>
                  {isTower && <button style={{ ...font, fontSize: 12, padding: '6px 11px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' }} disabled={busy === p.threadId}
                    onClick={() => run(p.threadId, () => movePassenger(memberId, memberName, p.threadId, null, 'Sent back to triage at post time'))}>Back to triage</button>}
              </>
            </div>
          </div>
        ))}
      </div>
      {err && <div style={{ ...font, fontSize: 12, color: '#c0392b', marginBottom: 10 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onChanged} style={{ ...font, fontSize: 12, padding: '7px 14px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' }}>Check again</button>
        <button onClick={onClose} style={{ ...font, fontSize: 12, padding: '7px 14px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' }}>Close</button>
      </div>
    </>
  )
}
